import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import { v4 as uuidv4 } from "uuid";
import VoiceList from "./VoiceList";

const VoiceTranscription = () => {
  const theme = useTheme();
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("en-US");
  const [stream, setStream] = useState(null);
  const [error, setError] = useState("");
  const [audioChunks, setAudioChunks] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [refreshList, setRefreshList] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [recordingTime, setRecordingTime] = useState(0);

  const { chatbotId: paramsChatbotId } = useParams();

  const recordingTimeout = useRef(null);
  const recordingInterval = useRef(null);
  const audioChunksRef = useRef([]);
  const resultIdRef = useRef(null);

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("voices", "view");
        const modifyPerm = await hasPermission("voices", "add|edit");
        const deletePerm = await hasPermission("voices", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  const userData = JSON.parse(localStorage.getItem("user") || "null");
  const time_in_m = userData?.subscription?.usage?.audio_time_limit || 2;
  const time_in_ms = time_in_m * 60 * 1000 || 120000;

  useEffect(() => {
    document.title = "Audio Recording | KlonIT Web Application";
    fetchLanguages();

    return () => {
      if (recordingTimeout.current) {
        clearTimeout(recordingTimeout.current);
      }
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const fetchLanguages = async () => {
    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.get(`/get-languages`, { params });

      const uniqueLanguages = [];
      const languageValues = new Set();
      const responseData =
        typeof response.data.data === "string"
          ? JSON.parse(response.data.data)
          : response.data.data;

      responseData.forEach((lang) => {
        if (!languageValues.has(lang.language)) {
          languageValues.add(lang.language);
          uniqueLanguages.push({
            value: lang.language,
            label: `${lang.country} (${lang.language})`,
          });
        }
      });

      setLanguages(uniqueLanguages);

      if (!selectedLanguage && uniqueLanguages.length > 0) {
        const defaultLang = uniqueLanguages.find(
          (lang) => lang.value === "en-US"
        );
        if (defaultLang) {
          setSelectedLanguage(defaultLang.value);
        }
      }
    } catch (error) {
      console.error("Error fetching languages:", error);
      setError("Failed to fetch languages. Please try again.");
    }
  };

  const handleLanguageChange = (selectedOption) => {
    setSelectedLanguage(selectedOption.value);
    setError("");
  };

  // Custom styles for react-select to support dark mode
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      borderColor: state.isFocused
        ? theme.palette.primary.main
        : theme.palette.mode === "dark"
        ? "#4a5568"
        : "#d1d5db",
      color: theme.palette.text.primary,
      minHeight: "40px",
      boxShadow: state.isFocused
        ? `0 0 0 1px ${theme.palette.primary.main}`
        : "none",
      "&:hover": {
        borderColor: theme.palette.primary.main,
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${
        theme.palette.mode === "dark" ? "#4a5568" : "#d1d5db"
      }`,
      boxShadow: theme.palette.mode === "dark"
        ? "0 4px 6px rgba(0, 0, 0, 0.3)"
        : "0 4px 6px rgba(0, 0, 0, 0.1)",
      maxHeight: "300px",
      overflow: "hidden",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "300px",
      overflowY: "auto",
      backgroundColor: theme.palette.background.paper,
      // Custom scrollbar styling
      "&::-webkit-scrollbar": {
        width: "8px",
      },
      "&::-webkit-scrollbar-track": {
        background: theme.palette.mode === "dark" ? "#2d3748" : "#f1f1f1",
      },
      "&::-webkit-scrollbar-thumb": {
        background: theme.palette.mode === "dark" ? "#4a5568" : "#888",
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: theme.palette.mode === "dark" ? "#718096" : "#555",
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? theme.palette.primary.main
        : state.isFocused
        ? theme.palette.mode === "dark"
          ? "#2d3748"
          : "#f3f4f6"
        : theme.palette.background.paper,
      color: state.isSelected
        ? "#ffffff"
        : theme.palette.text.primary,
      cursor: "pointer",
      "&:active": {
        backgroundColor: theme.palette.primary.main,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
    }),
    input: (provided) => ({
      ...provided,
      color: theme.palette.text.primary,
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme.palette.text.secondary,
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: theme.palette.text.secondary,
      "&:hover": {
        color: theme.palette.text.primary,
      },
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      backgroundColor: theme.palette.mode === "dark" ? "#4a5568" : "#d1d5db",
    }),
  };

  const startRecording = async () => {
    try {
      setIsRecording(true);
      setError("");
      setRecordingTime(0);
      resultIdRef.current = uuidv4();
      audioChunksRef.current = [];
      setAudioChunks([]);

      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1 },
        video: false,
      });

      const recorder = new MediaRecorder(audioStream, {
        mimeType: "audio/webm;codecs=opus",
      });

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          setAudioChunks((prev) => [...prev, event.data]);
        }
      };

      recorder.onstop = async () => {
        audioStream.getTracks().forEach((track) => track.stop());
        setIsRecording(false);
        setStream(null);

        if (recordingTimeout.current) {
          clearTimeout(recordingTimeout.current);
          recordingTimeout.current = null;
        }
        if (recordingInterval.current) {
          clearInterval(recordingInterval.current);
          recordingInterval.current = null;
        }

        if (audioChunksRef.current.length > 0) {
          setIsUploading(true);
          await handleFileUpload(audioChunksRef.current, resultIdRef.current);
        }
      };

      setMediaRecorder(recorder);
      setStream(audioStream);
      recorder.start(100);

      recordingTimeout.current = setTimeout(() => {
        if (recorder.state === "recording") {
          recorder.stop();
        }
      }, time_in_ms);

      recordingInterval.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error starting recording:", err);
      setError(`Failed to start recording: ${err.message}`);
      setIsRecording(false);
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    }
  };

  const stopRecording = () => {
    if (recordingTimeout.current) {
      clearTimeout(recordingTimeout.current);
      recordingTimeout.current = null;
    }

    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
    }

    if (mediaRecorder && mediaRecorder.state === "recording") {
      mediaRecorder.stop();
    }

    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const handleFileUpload = async (chunks, resultId) => {
    if (!chunks || chunks.length === 0) {
      setError("No audio recorded.");
      setIsUploading(false);
      return;
    }

    try {
      const audioBlob = new Blob(chunks, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", audioBlob, `recording_${resultId}.webm`);
      formData.append("lang_type", selectedLanguage);

      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.post(`/upload-voice`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params,
      });

      setError(response.data.message);
      setRefreshList((prev) => !prev);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || err.message || "Upload failed";
      setError(`Error uploading audio: ${errorMessage}`);
    } finally {
      setIsUploading(false);
      setAudioChunks([]);
      audioChunksRef.current = [];
    }
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4}>
        <CircularProgress size={30} />
        <Typography mt={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6" fontWeight={700}>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to view voice data. Please contact your
            administrator.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{
          p: 3,
          mt: 10,
          mx: "auto",
          maxWidth: "1200px",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
         Manage  <strong>Audio</strong>
        </Typography>
        <Divider sx={{ mb: 3 }} />

       <Box sx={{ mb: 3, textAlign: "center"}}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            margin: '0 auto',
            maxWidth: 400 
          }}>
            <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">
              Record Voice
            </Typography>

            <Box sx={{ mb: 2, width: '100%' }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Select Language
              </Typography>
              <Select
                options={languages}
                onChange={handleLanguageChange}
                value={languages.find(
                  (option) => option.value === selectedLanguage
                )}
                isDisabled={isRecording || isUploading}
                styles={customSelectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Box>

            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" justifyContent="center">
              {canModify && (
                <Button
                  variant="contained"
                  color={isRecording ? "error" : "primary"}
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isUploading}
                  startIcon={
                    isRecording ? <StopCircleIcon /> : <MicIcon />
                  }
                >
                  {isRecording ? "Stop Recording" : "Start Recording"}
                </Button>
              )}

              {isUploading && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Transcribing...</Typography>
                </Box>
              )}
            </Box>

            {isRecording && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Recording Time:{" "}
                  {Math.floor(recordingTime / 60)
                    .toString()
                    .padStart(2, "0")}
                  :{(recordingTime % 60).toString().padStart(2, "0")}
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Maximum Available Recording Time: {time_in_m} minute
                {time_in_m !== 1 ? "s" : ""}
              </Typography>
            </Box>

            {error && (
              <Alert
                severity={
                  error.includes("success") || error.includes("successfully")
                    ? "success"
                    : error.includes("Upload") || error.includes("Failed")
                    ? "error"
                    : "warning"
                }
                sx={{ mt: 2, width: '100%' }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">
            Voice Recordings
          </Typography>
          <VoiceList
            refresh={refreshList}
            chatbotId={paramsChatbotId}
            permission={{
              canView: canView,
              canModify: canModify,
              canDelete: canDelete,
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default VoiceTranscription;